<script setup lang="ts">
import type { SubjectCode } from '~/models/Subject'
import type { Product } from '~/models/Product'
import { useRouter }  from '#imports'
import { useI18n } from 'vue-i18n'
import { computed, onMounted, ref, watch } from 'vue'
import { RadioGroupRoot } from 'reka-ui'
import { storeToRefs } from 'pinia'
import { sortBySubjectIndex } from '~/utils/subjectSorter'
import { sortByNameGradeSubject } from '~/utils/productSorter'
import arrayUtils from '~/utils/arrayUtils'
import useProductStore from '~/stores/product'
import { useAuthStore } from '~/stores/auth'
import { useUrlBuilder } from '~/composables/useUrlBuilder'
import useSlugify from '~/composables/useSlugify'
import useProductHelper from '~/composables/useProductHelper'
import Button from '~/components/ui/Button.vue'
import AuRadio from '~/components/ui/AuRadio.vue'
import DefaultMessages from '~/components/home/DefaultMessages.vue'
import DefaultIllustration from '~/components/home/DefaultIllustration.vue'
import DefaultGreetings from '~/components/home/DefaultGreetings.vue'

const termExamSubjects: SubjectCode[] = ['ENG']

const subjectGroup: { [key: string]: Array<SubjectCode> } = {
  NOR: ['NOR'],
  MAT: ['MAT'],
  LANG: ['ENG', 'SPA', 'TYS', 'FRA', 'FSP'],
  REAL: ['NAT', 'GFG', 'KJE', 'INF', 'FYS', 'GEO'],
  SAF: ['SAF', 'HIS', 'SAK', 'SOS', 'SAG', 'SOK', 'POM', 'HEL', 'PSY', 'REL', 'KRL', 'GEO'],
  VOK: ['VOK'],
}

const { t } = useI18n()
const router = useRouter()
const { intersect, unique } = arrayUtils()
const authStore = useAuthStore()
const { userSubjectsByGrades, userGrades, user } = storeToRefs(authStore)
const { products } = storeToRefs(useProductStore())
const { isProductVgs, isExams, isLegacyView, isUnderDevelopment } = useProductHelper()
const { resolveBaseUrl } = useUrlBuilder()
const { slugify } = useSlugify()

const selectedSubjectGroup = ref<string>()

const allProducts = computed(() => products.value.filter(isProductVgs).map(product => isExams(product)
  ? { ...product, subjects: product.subjects.filter(s => termExamSubjects.includes(s)) }
  : product
))

const filteredSubjects = computed(() => selectedSubjectGroup.value ? (subjectGroup[selectedSubjectGroup.value] ?? []) : userSubjectsByGrades.value)

const productSubjects = computed(() => allProducts.value
  .flatMap(({ subjects }) => subjects)
  .filter(unique)
  .sort(sortBySubjectIndex))

const filteredGroups = computed(() => Object.keys(subjectGroup)
  .filter(key => intersect(subjectGroup[key], productSubjects.value).length > 0))

const hasUserGrades = (product: Product) => !product.grades.length || intersect(product.grades, userGrades.value).length > 0
const hasUserSubjects = (product: Product) => !product.subjects.length || intersect(product.subjects, filteredSubjects.value).length > 0
const hasUserSettings = (product: Product) => hasUserGrades(product) && hasUserSubjects(product)

const filteredProducts = computed(() => allProducts.value
  .filter(product => hasUserSettings(product) && !isUnderDevelopment(product))
  .sort(sortByNameGradeSubject))

const underDevelopmentProduct = computed(() => allProducts.value.find(isUnderDevelopment))

const setSubjectGroup = (group: string) => localStorage.setItem('subject-group', group)

const clearSubjectGroup = () => {
  localStorage.removeItem('subject-group')
  selectedSubjectGroup.value = undefined
}

const buildLegacyProductUrl = (product: Product) => {
  if (!product.aunivers || !product.aunivers.urlAlias) {
    return router.resolve({ name: 'notfound' }).href
  }
  const url = new URL(product.aunivers.urlAlias)
  return `${import.meta.env.VITE_AUNIVERS_SITEURL}${url.pathname}`
}

onMounted(() => {
  const cachedGroup = localStorage.getItem('subject-group')

  if (cachedGroup === 'NAT') {
    selectedSubjectGroup.value = 'REAL'
  } else if (cachedGroup && (cachedGroup in subjectGroup)) {
    selectedSubjectGroup.value = cachedGroup
  }
})

watch(userGrades, () => clearSubjectGroup())
</script>

<template>
  <header class="bg-green-40 relative mb-12 flex-col justify-center text-white">
    <div class="max-w-screen-au xs:pb-14 relative z-20 mx-auto size-full px-4 pt-40 pb-10 transition-all sm:px-8 sm:pt-28">
      <DefaultMessages class="mb-6 !px-0" />
      <h1
        class="sr-only"
        v-text="t('home.title')"
      />
      <DefaultGreetings
        v-if="user"
        :name="user.fullName"
      />
      <div
        v-if="filteredProducts.length"
        class="mt-4 md:max-w-[74%]"
      >
        <div class="max-w-max rounded-lg bg-green-50 p-6">
          <p class="mb-3 font-medium">
            {{ t('search.filters.filterBySubject') }}
          </p>
          <RadioGroupRoot
            v-if="filteredGroups.length"
            v-model="selectedSubjectGroup"
            as="ul"
            class="flex flex-wrap items-center gap-3"
            @update:model-value="group => setSubjectGroup(group)"
          >
            <li
              v-for="groupCode in filteredGroups"
              :key="`group-${groupCode}`"
            >
              <Button
                type="label"
                :variant="groupCode === selectedSubjectGroup ? 'solid' : 'outline'"
                shade="light"
              >
                <AuRadio
                  :id="groupCode"
                  :value="groupCode"
                  :shade="groupCode === selectedSubjectGroup ? 'dark' : 'light'"
                />
                {{ t(`metadata.subjects.${groupCode}`) }}
              </Button>
            </li>
          </RadioGroupRoot>
          <div v-if="selectedSubjectGroup" class="font-md mt-4 flex justify-end  p-1 text-sm font-semibold">
            <button
              class="hover:underline focus-visible:ring"
              @click.prevent="clearSubjectGroup"
              v-text="t('clear')"
            />
          </div>
        </div>
        <p
          v-if="filteredProducts.length"
          class="my-4 font-medium"
          v-text="t('home.yourProducts')"
        />
        <ul
          v-if="filteredProducts.length"
          class="relative z-10 -mx-4 flex h-fit flex-wrap gap-3 overflow-x-visible px-4 pb-4"
        >
          <li
            v-for="product in filteredProducts"
            :key="product.ean"
          >
            <Button
              v-if="!isLegacyView(product)"
              color="white"
              size="lg"
              :to="{ name: 'product',
                     params: {
                       locationId: product.aunivers.locationId,
                       slug: slugify(product.aunivers.name),
                     } }"
            >
              {{ product.aunivers.name }}
            </Button>
            <Button
              v-else
              color="white"
              size="lg"
              :href="buildLegacyProductUrl(product)"
            >
              {{ product.aunivers.name }}
            </Button>
          </li>
        </ul>
        <router-link
          v-if="underDevelopmentProduct"
          :to="{ name: 'product',
                 params: {
                   locationId: underDevelopmentProduct.aunivers.locationId,
                   slug: slugify(underDevelopmentProduct.aunivers.name),
                 } }"
          class="hover:text-green-10 text-green-5 underline underline-offset-2 focus-visible:ring"
        >
          {{ underDevelopmentProduct.aunivers.name }}
        </router-link>
        <DefaultIllustration />
      </div>
    </div>
  </header>
</template>
